import { Injectable, computed, inject } from '@angular/core';

import { IconName } from '@shared/components/icon/icon.component';

import { RoleKind } from '../models/brijjsports.enums';
import { SportNavConfig, SportNavOverride } from '../sports/sport.types';
import { SportsService } from '../sports/sports.service';
import { BrijjSportsStateService } from '../state/brijjsports-state.service';
import { TenantContextService } from '../tenant/tenant-context.service';

export interface NavItem {
  label: string;
  icon: IconName;
  route: string;
  /**
   * When set, the item only appears while one of these sports is active.
   * Empty / undefined means the item shows regardless of current sport.
   */
  sportKeys?: readonly string[];
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

const OWNER_NAV: NavSection[] = [
  {
    title: '',
    items: [
      { label: 'Dashboard', icon: 'home', route: '/owner/dashboard' },
      { label: 'Sports', icon: 'grid', route: '/owner/sports' },
      { label: 'Athletes', icon: 'users', route: '/owner/athletes' },
      { label: 'Coaches', icon: 'award', route: '/owner/coaches' },
      { label: 'Parents', icon: 'user', route: '/owner/parents' },
      { label: 'Programs', icon: 'layers', route: '/owner/programs' },
      { label: 'Teams', icon: 'users', route: '/owner/teams' },
      { label: 'Schedule', icon: 'calendar', route: '/owner/events' },
      { label: 'Facilities', icon: 'building', route: '/owner/facilities' },
      { label: 'Compliance', icon: 'check-circle', route: '/owner/compliance' }
    ]
  },
  {
    title: 'Operations',
    items: [
      { label: 'Analytics', icon: 'bar-chart', route: '/owner/analytics' },
      { label: 'Reports', icon: 'download', route: '/owner/reports' },
      { label: 'Documents', icon: 'file-text', route: '/owner/documents' },
      { label: 'Staff', icon: 'clock', route: '/owner/staff' },
      { label: 'Check-in kiosk', icon: 'scan', route: '/owner/checkin' }
    ]
  },
  {
    title: 'Diamond sports',
    items: [
      { label: 'Pitch counts', icon: 'bar-chart', route: '/owner/pitch-counts', sportKeys: ['baseball', 'softball'] }
    ]
  },
  {
    title: 'Finance',
    items: [
      { label: 'Invoices', icon: 'file-text', route: '/owner/billing' },
      { label: 'Registrations', icon: 'user', route: '/owner/registrations' }
    ]
  },
  {
    title: 'Manage',
    items: [{ label: 'Settings', icon: 'settings', route: '/owner/settings' }]
  }
];

const COACH_NAV: NavSection[] = [
  {
    title: '',
    items: [
      { label: 'Dashboard', icon: 'home', route: '/coach/dashboard' },
      { label: 'Teams', icon: 'layers', route: '/coach/teams' },
      { label: 'Roster', icon: 'users', route: '/coach/roster' },
      { label: 'Schedule', icon: 'calendar', route: '/coach/schedule' },
      { label: 'Attendance', icon: 'check-circle', route: '/coach/attendance' },
      { label: 'Scoring', icon: 'trending-up', route: '/coach/scoring' }
    ]
  },
  {
    title: 'Coaching',
    items: [
      { label: 'Drill library', icon: 'list', route: '/coach/drills' },
      { label: 'Practice plans', icon: 'file-text', route: '/coach/practice-plans' },
      { label: 'Tournaments', icon: 'award', route: '/coach/tournaments' },
      { label: 'Skills', icon: 'star', route: '/coach/skills' }
    ]
  },
  {
    title: 'Diamond sports',
    items: [
      { label: 'Lineups', icon: 'list', route: '/coach/lineups', sportKeys: ['baseball', 'softball'] },
      { label: 'Availability', icon: 'check-circle', route: '/coach/availability' },
      { label: 'Messages', icon: 'mail', route: '/coach/messages' },
      { label: 'Brackets', icon: 'award', route: '/coach/brackets', sportKeys: ['baseball', 'softball'] }
    ]
  }
];

const PARENT_NAV: NavSection[] = [
  {
    title: '',
    items: [
      { label: 'Dashboard', icon: 'home', route: '/parent/dashboard' },
      { label: 'Children', icon: 'users', route: '/parent/children' },
      { label: 'Schedule', icon: 'calendar', route: '/parent/schedule' },
      { label: 'Attendance', icon: 'check-circle', route: '/parent/attendance' }
    ]
  },
  {
    title: 'Communicate',
    items: [
      { label: 'Messages', icon: 'mail', route: '/parent/messages' }
    ]
  },
  {
    title: 'Finance',
    items: [
      { label: 'Billing', icon: 'file-text', route: '/parent/billing' }
    ]
  }
];

const ATHLETE_NAV: NavSection[] = [
  {
    title: '',
    items: [
      { label: 'Dashboard', icon: 'home', route: '/athlete/dashboard' },
      { label: 'Scores', icon: 'trending-up', route: '/athlete/scores' },
      { label: 'Schedule', icon: 'calendar', route: '/athlete/schedule' },
      { label: 'Goals', icon: 'star', route: '/athlete/goals' },
      { label: 'Attendance', icon: 'check-circle', route: '/athlete/attendance' },
      { label: 'Journal', icon: 'file-text', route: '/athlete/journal' }
    ]
  },
  {
    title: 'Diamond sports',
    items: [
      { label: 'Availability', icon: 'check-circle', route: '/athlete/availability' },
      { label: 'Messages', icon: 'mail', route: '/athlete/messages' }
    ]
  }
];

const ADMIN_NAV: NavSection[] = [
  {
    title: '',
    items: [{ label: 'Tenants', icon: 'layers', route: '/admin/tenants' }]
  },
  {
    title: 'Onboard',
    items: [{ label: 'New tenant', icon: 'star', route: '/admin/tenants/new' }]
  }
];

const NAV_BY_ROLE: Record<RoleKind, NavSection[]> = {
  [RoleKind.Owner]: OWNER_NAV,
  [RoleKind.Coach]: COACH_NAV,
  [RoleKind.Parent]: PARENT_NAV,
  [RoleKind.Athlete]: ATHLETE_NAV
};

@Injectable({ providedIn: 'root' })
export class RoleNavService {
  private readonly state = inject(BrijjSportsStateService);
  private readonly tenants = inject(TenantContextService);
  private readonly sports = inject(SportsService);

  readonly sections = computed<NavSection[]>(() => {
    if (this.tenants.isRoot()) return ADMIN_NAV;
    const role = this.state.currentRole();
    const base = NAV_BY_ROLE[role] ?? [];
    const sportFiltered = this.filterBySport(base);
    return this.applySportOverrides(sportFiltered, role);
  });

  /**
   * Drop nav items whose `sportKeys` don't include the active sport. Sections
   * left empty are removed so empty groupings don't clutter the sidebar.
   */
  private filterBySport(base: NavSection[]): NavSection[] {
    const currentKey = this.sports.currentSport().key;
    return base
      .map(section => ({
        ...section,
        items: section.items.filter(
          item => !item.sportKeys?.length || item.sportKeys.includes(currentKey)
        )
      }))
      .filter(section => section.items.length > 0);
  }

  /** Mobile bottom nav — first 4 items always visible. */
  readonly bottomNav = computed<NavItem[]>(() => {
    const all = this.sections().flatMap(s => s.items);
    return all.slice(0, 4);
  });

  /** Items beyond the bottom-nav cap, surfaced via a "More" overflow menu. */
  readonly bottomNavMore = computed<NavItem[]>(() => {
    const all = this.sections().flatMap(s => s.items);
    return all.slice(4);
  });

  navForRole(role: RoleKind): NavSection[] {
    return NAV_BY_ROLE[role] ?? [];
  }

  defaultRouteFor(role: RoleKind): string {
    return NAV_BY_ROLE[role][0]?.items[0]?.route ?? '/welcome';
  }

  /**
   * Re-label / re-icon role nav items using the active sport's `nav` config.
   * Items without a matching override pass through unchanged. Pure — produces
   * a fresh array every call so OnPush views update.
   */
  private applySportOverrides(base: NavSection[], role: RoleKind): NavSection[] {
    const sport = this.sports.currentSport();
    const overrides = pickRoleOverrides(sport.nav, role);
    if (!overrides?.length) return base;
    const byRoute = new Map(overrides.map(o => [o.route, o]));
    return base.map(sec => ({
      ...sec,
      items: sec.items.map(item => {
        const o = byRoute.get(item.route);
        if (!o) return item;
        return {
          ...item,
          label: o.label ?? item.label,
          icon: (o.icon as IconName | undefined) ?? item.icon
        };
      })
    }));
  }
}

function pickRoleOverrides(nav: SportNavConfig | undefined, role: RoleKind): SportNavOverride[] | undefined {
  if (!nav) return undefined;
  switch (role) {
    case RoleKind.Owner:
      return nav.owner;
    case RoleKind.Coach:
      return nav.coach;
    case RoleKind.Athlete:
      return nav.athlete;
    case RoleKind.Parent:
      return nav.parent;
    default:
      return undefined;
  }
}
